import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  BaseEdge,
  ControlButton,
  Controls,
  EdgeLabelRenderer,
  MiniMap,
  addEdge,
  getBezierPath,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import "@/pages/outbound/WorkflowCanvas.css";
import {
  Linkedin, Mail, MessageCircle, MessageSquare, Mic, Clock, GitBranch, Goal, Workflow, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  WORKFLOW_DRAG_MIME,
  WORKFLOW_GRID_SIZE,
  CONDITION_BRANCH_IF,
  CONDITION_BRANCH_ELSE,
  EDGE_ARROW_LENGTH,
  EDGE_HANDLE_RADIUS,
  arrowHeadPoints,
  centerDropOnCursor,
  createPaletteNode,
  decodeWorkflowDragPayload,
  edgeWouldCreateCycle,
  normalizeEdges,
  offsetHandlePoint,
  removeWorkflowNode,
  withWorkflowNodeFlags,
  formatWaitDuration,
  conditionBranchLabel,
  isGoalWorkflowNodeType,
} from "@/lib/campaignWorkflow";
import { scheduleAfterLayout } from "@/lib/resizeObserverLoop";
import { toast } from "sonner";

const HANDLE_CLASS = "!bg-slate-400 !w-2.5 !h-2.5 !border-2 !border-white !z-[1]";

const nodeStyles = {
  start: { bg: "bg-slate-900", text: "text-white", ring: "ring-slate-700", icon: Workflow, label: "ENROLLMENT" },
  linkedin: { bg: "bg-blue-50", text: "text-blue-800", ring: "ring-blue-200", icon: Linkedin, label: "LINKEDIN" },
  email: { bg: "bg-indigo-50", text: "text-indigo-800", ring: "ring-indigo-200", icon: Mail, label: "EMAIL" },
  whatsapp: { bg: "bg-emerald-50", text: "text-emerald-800", ring: "ring-emerald-200", icon: MessageCircle, label: "WHATSAPP" },
  sms: { bg: "bg-violet-50", text: "text-violet-800", ring: "ring-violet-200", icon: MessageSquare, label: "SMS" },
  telegram: { bg: "bg-sky-50", text: "text-sky-800", ring: "ring-sky-200", icon: MessageCircle, label: "TELEGRAM" },
  voice: { bg: "bg-rose-50", text: "text-rose-800", ring: "ring-rose-200", icon: Mic, label: "AI VOICE" },
  wait: { bg: "bg-amber-50", text: "text-amber-800", ring: "ring-amber-200", icon: Clock, label: "WAIT" },
  condition: { bg: "bg-white", text: "text-slate-800", ring: "ring-slate-300", icon: GitBranch, label: "IF / ELSE" },
  ab: { bg: "bg-white", text: "text-slate-800", ring: "ring-slate-300", icon: GitBranch, label: "A/B SPLIT" },
  goal: { bg: "bg-emerald-600", text: "text-white", ring: "ring-emerald-700", icon: Goal, label: "GOAL" },
};

function FlowNode({ id, data, selected, type }) {
  const { deleteElements } = useReactFlow();
  const s = nodeStyles[type] || nodeStyles.email;
  const Icon = s.icon;
  const canDelete = !data?.readOnly && type !== "start" && id !== "start";

  const handleDelete = (event) => {
    event.stopPropagation();
    deleteElements({ nodes: [{ id }] });
  };

  return (
    <div
      className={cn(
        "relative rounded-xl border-2 px-3 py-2 w-[250px] shadow-sm transition-all",
        type === "condition" && "mb-3",
        s.bg, "border-transparent",
        selected ? "ring-2 ring-indigo-500" : `ring-1 ${s.ring}`,
        data?.configIncomplete && !selected && "ring-2 ring-amber-400 border-amber-300",
        data?.justAdded && "animate-in fade-in zoom-in-95 duration-200",
      )}
      data-testid={`flow-node-${type}`}
      data-incomplete={data?.configIncomplete ? "true" : "false"}
    >
      {canDelete && selected && (
        <button
          type="button"
          className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 shadow-sm hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200"
          title="Remove step"
          aria-label="Remove step"
          data-testid={`flow-node-delete-${id}`}
          onClick={handleDelete}
        >
          <X size={11} />
        </button>
      )}
      {type !== "start" && (
        <Handle type="target" position={Position.Top} className={HANDLE_CLASS} />
      )}
      <div className="flex items-center gap-1.5">
        <span className={cn("inline-flex items-center justify-center rounded h-5 w-5", s.bg === "bg-white" ? "bg-slate-100" : "bg-white/60")}>
          <Icon size={11} className={s.text} />
        </span>
        <span className={cn("text-[9.5px] font-semibold uppercase tracking-wider", s.text)}>{s.label}</span>
      </div>
      <div className={cn("mt-1 text-[12px] font-medium leading-snug", s.text)}>{data.label}</div>
      {data.subject && (
        <div className={cn("text-[10.5px] mt-0.5 line-clamp-1", s.text, "opacity-75")}>{data.subject}</div>
      )}
      {!data.subject && data.message && type !== "wait" && (
        <div className={cn("text-[10.5px] mt-0.5 line-clamp-1", s.text, "opacity-75")}>{data.message}</div>
      )}
      {data?.configIncomplete && (
        <div className="mt-1 text-[10px] font-medium text-amber-700" data-testid="flow-node-needs-content">
          Needs content
        </div>
      )}
      {type === "wait" && (
        <div className={cn("text-[10.5px] mt-0.5", s.text, "opacity-75")} data-testid="flow-node-wait-duration">
          {formatWaitDuration(data)}
        </div>
      )}
      {type === "condition" && data?.condition && (
        <div className={cn("text-[10.5px] mt-0.5 line-clamp-2", s.text, "opacity-75")} data-testid="flow-node-condition-summary">
          {data.condition}
        </div>
      )}
      {type === "condition" && (
        <>
          <Handle
            type="source"
            id={CONDITION_BRANCH_IF}
            position={Position.Bottom}
            className="!bg-emerald-500 !w-2.5 !h-2.5 !border-2 !border-white !z-[1]"
            style={{ left: "28%" }}
            data-testid="flow-handle-if"
          />
          <Handle
            type="source"
            id={CONDITION_BRANCH_ELSE}
            position={Position.Bottom}
            className={HANDLE_CLASS}
            style={{ left: "72%" }}
            data-testid="flow-handle-else"
          />
          <span
            className="pointer-events-none absolute left-[28%] -bottom-4 -translate-x-1/2 text-[9px] font-semibold uppercase tracking-wide text-emerald-600"
          >
            If
          </span>
          <span
            className="pointer-events-none absolute left-[72%] -bottom-4 -translate-x-1/2 text-[9px] font-semibold uppercase tracking-wide text-slate-500"
          >
            Else
          </span>
        </>
      )}
      {/* Goal ends the workflow — no outgoing (bottom) source handle. */}
      {type !== "goal" && type !== "condition" && (
        <Handle type="source" position={Position.Bottom} className={HANDLE_CLASS} />
      )}
    </div>
  );
}

FlowNode.propTypes = {
  id: PropTypes.string,
  selected: PropTypes.bool,
  type: PropTypes.string.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string,
    subject: PropTypes.string,
    message: PropTypes.string,
    justAdded: PropTypes.bool,
    configIncomplete: PropTypes.bool,
    readOnly: PropTypes.bool,
    condition: PropTypes.string,
    wait_days: PropTypes.number,
    wait_hours: PropTypes.number,
    wait_minutes: PropTypes.number,
  }),
};

function RemovableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  selected,
  data,
  label,
  sourceHandle,
}) {
  const { deleteElements } = useReactFlow();
  const stroke = selected ? "#6366f1" : (style?.stroke || "#94a3b8");
  // Tip sits on the handle rim (not short of it, not through the center).
  // Path ends at the arrow base so the stroke meets the head cleanly.
  const tip = offsetHandlePoint(targetX, targetY, targetPosition, EDGE_HANDLE_RADIUS);
  const pathEnd = offsetHandlePoint(
    targetX,
    targetY,
    targetPosition,
    EDGE_HANDLE_RADIUS + EDGE_ARROW_LENGTH - 1,
  );
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX: pathEnd.x,
    targetY: pathEnd.y,
    targetPosition,
    curvature: 0.18,
  });
  const canRemove = selected && !data?.readOnly;
  const branchLabel = label || conditionBranchLabel(sourceHandle || data?.branch);

  const handleDelete = (event) => {
    event.stopPropagation();
    event.preventDefault();
    deleteElements({ edges: [{ id }] });
  };

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke,
          strokeWidth: selected ? 2.5 : (style?.strokeWidth || 1.5),
        }}
        interactionWidth={24}
      />
      <polygon
        points={arrowHeadPoints(tip.x, tip.y, targetX, targetY)}
        fill={stroke}
        stroke={stroke}
        strokeWidth={1}
        strokeLinejoin="round"
        data-testid={`flow-edge-arrow-${id}`}
      />
      {(branchLabel || canRemove) && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: canRemove ? "all" : "none",
            }}
          >
            <div className="flex items-center gap-1">
              {branchLabel ? (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                    sourceHandle === CONDITION_BRANCH_IF || sourceHandle === "yes"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-slate-100 text-slate-600",
                  )}
                  data-testid={`flow-edge-label-${id}`}
                >
                  {branchLabel}
                </span>
              ) : null}
              {canRemove && (
                <button
                  type="button"
                  title="Remove edge"
                  aria-label="Remove edge"
                  data-testid={`flow-edge-delete-${id}`}
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 shadow-sm hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200"
                  onClick={handleDelete}
                >
                  <X size={11} />
                </button>
              )}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

RemovableEdge.propTypes = {
  id: PropTypes.string,
  sourceX: PropTypes.number,
  sourceY: PropTypes.number,
  targetX: PropTypes.number,
  targetY: PropTypes.number,
  sourcePosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  targetPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  selected: PropTypes.bool,
  label: PropTypes.string,
  sourceHandle: PropTypes.string,
  data: PropTypes.shape({
    readOnly: PropTypes.bool,
    branch: PropTypes.string,
  }),
};

const nodeTypes = Object.fromEntries(
  Object.keys(nodeStyles).map((k) => [k, (p) => <FlowNode {...p} type={k} />]),
);

const edgeTypes = {
  removable: RemovableEdge,
};

const FIT_VIEW_OPTIONS = { padding: 0.28, maxZoom: 1.5, minZoom: 0.25 };

/** Lordicon system/regular/135-fit-screen — fullscreen / fit-view mark. */
function FitScreenIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="42 83 416 334"
      width={16}
      height={13}
      aria-hidden="true"
      className="!max-h-none !max-w-none"
    >
      <path
        fill="currentColor"
        d="M57.294 187.526c-8.644 0-15.651-7.007-15.651-15.65v-52.083c0-20.117 16.367-36.483 36.484-36.483h52.083c8.644 0 15.651 7.007 15.651 15.65s-7.007 15.65-15.651 15.65H78.127a5.189 5.189 0 0 0-5.183 5.183v52.083c0 8.643-7.006 15.65-15.65 15.65zm72.917 229.169H78.128c-20.117 0-36.483-16.366-36.483-36.483v-50.781c0-8.643 7.006-15.65 15.65-15.65s15.65 7.007 15.65 15.65v50.781a5.19 5.19 0 0 0 5.183 5.183h52.083c8.644 0 15.65 7.007 15.65 15.65s-7.006 15.65-15.65 15.65zm291.668 0h-52.083c-8.644 0-15.651-7.007-15.651-15.65s7.007-15.65 15.651-15.65h52.083a5.189 5.189 0 0 0 5.183-5.183v-52.083c0-8.643 7.006-15.65 15.65-15.65s15.651 7.007 15.651 15.65v52.083c0 20.117-16.367 36.483-36.484 36.483zm20.833-229.168c-8.644 0-15.65-7.007-15.65-15.65v-52.083a5.19 5.19 0 0 0-5.183-5.184h-52.083c-8.644 0-15.651-7.006-15.651-15.649 0-8.643 7.007-15.65 15.651-15.65h52.083c20.117 0 36.484 16.366 36.484 36.483v52.083c0 8.643-7.007 15.65-15.651 15.65zm-62.499 166.668H119.795c-8.644 0-15.65-7.007-15.65-15.65V161.461c0-8.643 7.006-15.65 15.65-15.65h260.418c8.644 0 15.65 7.007 15.65 15.65v177.084c0 8.643-7.006 15.65-15.65 15.65zm-244.768-31.3h229.118V177.111H135.445v145.784z"
      />
    </svg>
  );
}

function FitScreenControl() {
  const { fitView } = useReactFlow();
  return (
    <ControlButton
      onClick={() => fitView(FIT_VIEW_OPTIONS)}
      title="Fullscreen"
      aria-label="Fullscreen"
      data-testid="workflow-fit-screen"
      className="[&_svg]:!max-h-[14px] [&_svg]:!max-w-[16px]"
    >
      <FitScreenIcon />
    </ControlButton>
  );
}

/** fitView once per layout mode (view/edit) — not on add/remove, which would reset zoom. */
function DeferredFitView({ layoutKey, hasNodes }) {
  const { fitView } = useReactFlow();
  const fittedLayoutRef = useRef(null);

  useEffect(() => {
    if (!hasNodes) return undefined;
    // Keep the user's pan/zoom when steps are added or removed.
    if (fittedLayoutRef.current === layoutKey) return undefined;
    fittedLayoutRef.current = layoutKey;
    // View → edit changes the grid (palette + inspector), so wait for layout settle.
    const settleMs = layoutKey === "edit" ? 80 : 0;
    return scheduleAfterLayout(() => {
      fitView(FIT_VIEW_OPTIONS);
    }, { settleMs });
  }, [fitView, layoutKey, hasNodes]);

  return null;
}

DeferredFitView.propTypes = {
  layoutKey: PropTypes.string.isRequired,
  hasNodes: PropTypes.bool.isRequired,
};

function WorkflowCanvasInner({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onWorkflowChange,
  onSelect,
  onAddNode,
  onNodeRemoved,
  readOnly = false,
}) {
  const { screenToFlowPosition } = useReactFlow();
  const [dropActive, setDropActive] = useState(false);
  const dragDepthRef = useRef(0);
  const displayNodes = useMemo(
    () => withWorkflowNodeFlags(nodes).map((n) => {
      // Normalize type so custom node renderers match (avoids RF default node handles).
      const type = String(n.type || "").toLowerCase();
      let next = type && type !== n.type ? { ...n, type } : n;
      if (readOnly) {
        next = { ...next, data: { ...next.data, readOnly: true } };
      }
      // Goal accepts incoming connections only — no bottom source handle / drag-out.
      if (isGoalWorkflowNodeType(next.type)) {
        next = { ...next, connectable: "target" };
      }
      return next;
    }),
    [nodes, readOnly],
  );
  const displayEdges = useMemo(
    () => normalizeEdges(edges).map((edge) => ({
      ...edge,
      type: "removable",
      data: { ...edge.data, readOnly },
    })),
    [edges, readOnly],
  );

  const applyRemoval = useCallback((removedIds) => {
    if (!removedIds.length || readOnly) return;
    const idSet = new Set(removedIds.filter((nid) => nid && nid !== "start"));
    if (!idSet.size) return;
    let nextNodes = displayNodes;
    let nextEdges = displayEdges;
    idSet.forEach((nid) => {
      const result = removeWorkflowNode(nid, nextNodes, nextEdges);
      if (result) {
        nextNodes = result.nodes;
        nextEdges = result.edges;
      }
    });
    onWorkflowChange?.({ nodes: nextNodes, edges: nextEdges });
    onNodeRemoved?.(Array.from(idSet));
  }, [readOnly, displayNodes, displayEdges, onWorkflowChange, onNodeRemoved]);

  const handleNodesChange = useCallback((changes) => {
    if (readOnly) return;
    const removedIds = changes
      .filter((c) => c.type === "remove" && c.id !== "start")
      .map((c) => c.id);
    const safeChanges = changes.filter(
      (c) => !(c.type === "remove" && c.id === "start"),
    );
    onNodesChange?.(safeChanges);
    if (removedIds.length) {
      applyRemoval(removedIds);
    }
  }, [readOnly, onNodesChange, applyRemoval]);

  const isValidConnection = useCallback((connection) => {
    const sourceNode = displayNodes.find((n) => n.id === connection?.source);
    return !isGoalWorkflowNodeType(sourceNode?.type);
  }, [displayNodes]);

  const onConnect = useCallback((params) => {
    if (readOnly) return;
    const sourceNode = displayNodes.find((n) => n.id === params.source);
    if (isGoalWorkflowNodeType(sourceNode?.type)) {
      toast.error("Goal is the end of the workflow — it cannot connect to other steps.");
      return;
    }
    if (edgeWouldCreateCycle(displayEdges, params.source, params.target)) {
      toast.error(
        "This connection would create a loop. Steps cannot connect back to an earlier step in the path.",
      );
      return;
    }
    const handle = params.sourceHandle || "";
    const branchLabel = conditionBranchLabel(handle);
    let stroke = "#94a3b8";
    if (handle === CONDITION_BRANCH_IF || handle === "yes") {
      stroke = "#10B981";
    }
    // One edge per branch handle from a condition node.
    let baseEdges = displayEdges;
    if (handle === CONDITION_BRANCH_IF || handle === CONDITION_BRANCH_ELSE) {
      baseEdges = displayEdges.filter(
        (e) => !(e.source === params.source && e.sourceHandle === handle),
      );
    }
    const next = addEdge({
      ...params,
      type: "removable",
      label: branchLabel || undefined,
      data: { branch: handle || undefined },
      style: { stroke },
      markerEnd: undefined,
    }, baseEdges);
    onWorkflowChange?.({ nodes: displayNodes, edges: next });
  }, [readOnly, displayEdges, displayNodes, onWorkflowChange]);

  const onNodesDelete = useCallback((deleted) => {
    if (readOnly || deleted.some((n) => n.id === "start")) return;
    applyRemoval(deleted.map((n) => n.id));
  }, [readOnly, applyRemoval]);

  const onDragEnter = useCallback((event) => {
    if (readOnly) return;
    if (!event.dataTransfer.types.includes(WORKFLOW_DRAG_MIME)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDropActive(true);
  }, [readOnly]);

  const onDragLeave = useCallback(() => {
    if (readOnly) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDropActive(false);
  }, [readOnly]);

  const onDragOver = useCallback((event) => {
    if (readOnly) return;
    if (!event.dataTransfer.types.includes(WORKFLOW_DRAG_MIME)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, [readOnly]);

  const onDrop = useCallback((event) => {
    if (readOnly) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDropActive(false);

    const payload = decodeWorkflowDragPayload(event.dataTransfer.getData(WORKFLOW_DRAG_MIME));
    if (!payload?.type) return;

    const flowPoint = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const position = centerDropOnCursor(flowPoint);
    const newNode = createPaletteNode(payload.type, payload.label, position, nodes);
    const nextNodes = [...displayNodes, newNode];
    onAddNode?.(newNode);
    onWorkflowChange?.({ nodes: nextNodes, edges: displayEdges });
  }, [readOnly, screenToFlowPosition, displayNodes, displayEdges, onAddNode, onWorkflowChange, nodes]);

  useEffect(() => {
    const hasNew = displayNodes.some((n) => n.data?.justAdded);
    if (!hasNew) return undefined;
    const timer = window.setTimeout(() => {
      onWorkflowChange?.({
        nodes: displayNodes.map((n) => (
          n.data?.justAdded ? { ...n, data: { ...n.data, justAdded: false } } : n
        )),
        edges: displayEdges,
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [displayNodes, displayEdges, onWorkflowChange]);

  return (
    <div // NOSONAR
      className={cn(
        "relative h-full w-full min-h-0 transition-colors",
        dropActive && !readOnly && "ring-2 ring-inset ring-indigo-400/70 bg-indigo-50/20",
      )}
      aria-label="Workflow canvas dropzone"
      data-testid="workflow-canvas-dropzone"
      data-drop-active={dropActive ? "true" : "false"}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dropActive && !readOnly && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="rounded-lg border-2 border-dashed border-indigo-400 bg-white/80 px-4 py-2 text-[12px] font-medium text-indigo-700 shadow-sm">
            Drop to add step
          </div>
        </div>
      )}
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{ type: "removable", interactionWidth: 24 }}
        onNodesChange={readOnly ? undefined : handleNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        onNodesDelete={onNodesDelete}
        onNodeClick={(_, n) => onSelect?.(n)}
        onPaneClick={() => onSelect?.(null)}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        nodesFocusable={!readOnly}
        elementsSelectable={!readOnly}
        edgesFocusable={!readOnly}
        deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
        snapToGrid={!readOnly}
        snapGrid={[WORKFLOW_GRID_SIZE, WORKFLOW_GRID_SIZE]}
        autoPanOnNodeDrag
        panOnDrag={[1, 2]}
        selectionOnDrag={!readOnly}
        panOnScroll
        panOnScrollMode="free"
        panOnScrollSpeed={0.85}
        zoomOnScroll={false}
        zoomOnPinch
        zoomOnDoubleClick
        preventScrolling
        minZoom={0.25}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.85 }}
        proOptions={{ hideAttribution: true }}
        className="bg-slate-50/30"
      >
        <Background gap={WORKFLOW_GRID_SIZE} size={1} color="#e2e8f0" />
        {/* MiniMap reads as broken gray boxes when few nodes exist on a narrow canvas. */}
        <MiniMap
          className="!hidden md:!block"
          pannable
          zoomable
          nodeStrokeColor="#94a3b8"
          nodeColor="#cbd5e1"
          maskColor="rgba(248,250,252,0.6)"
        />
        <Controls className="!m-2" showInteractive={false} showFitView={false}>
          <FitScreenControl />
        </Controls>
        <DeferredFitView
          layoutKey={readOnly ? "readonly" : "edit"}
          hasNodes={displayNodes.length > 0}
        />
      </ReactFlow>
    </div>
  );
}

const workflowNodePropType = PropTypes.shape({
  id: PropTypes.string,
  type: PropTypes.string,
  data: PropTypes.object,
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
});

const workflowEdgePropType = PropTypes.shape({
  id: PropTypes.string,
  source: PropTypes.string,
  target: PropTypes.string,
});

WorkflowCanvasInner.propTypes = {
  nodes: PropTypes.arrayOf(workflowNodePropType).isRequired,
  edges: PropTypes.arrayOf(workflowEdgePropType).isRequired,
  onNodesChange: PropTypes.func,
  onEdgesChange: PropTypes.func,
  onWorkflowChange: PropTypes.func,
  onSelect: PropTypes.func,
  onAddNode: PropTypes.func,
  onNodeRemoved: PropTypes.func,
  readOnly: PropTypes.bool,
};

export function WorkflowCanvas(props) {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

WorkflowCanvas.propTypes = {
  nodes: PropTypes.arrayOf(workflowNodePropType).isRequired,
  edges: PropTypes.arrayOf(workflowEdgePropType).isRequired,
  onNodesChange: PropTypes.func,
  onEdgesChange: PropTypes.func,
  onWorkflowChange: PropTypes.func,
  onSelect: PropTypes.func,
  onAddNode: PropTypes.func,
  onNodeRemoved: PropTypes.func,
  readOnly: PropTypes.bool,
};

export { nodeStyles };
